import { Component } from '@theme/component';

/**
 * Featured collections: tabbed product carousels with a scroll progress bar.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement[]} tracks
 * @property {HTMLElement[]} progress
 *
 * @extends {Component<Refs>}
 */
class FeaturedCollectionsComponent extends Component {
  requiredRefs = ['tabs', 'panels', 'tracks', 'progress'];

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    for (const tab of this.refs.tabs) {
      tab.addEventListener('click', () => this.select(tab), { signal });
      tab.addEventListener('keydown', this.#handleTabKeydown, { signal });
    }

    for (const track of this.refs.tracks) {
      track.addEventListener('scroll', () => this.#updateProgress(track), { passive: true, signal });
      track.addEventListener('pointerdown', this.#handleDragStart, { signal });
      track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
      track.addEventListener('click', this.#handleClickAfterDrag, { capture: true, signal });
    }

    this.#resizeObserver = new ResizeObserver(() => this.#updateAllProgress());
    for (const track of this.refs.tracks) this.#resizeObserver.observe(track);

    this.#updateAllProgress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Activates a tab and shows its panel.
   * @param {HTMLButtonElement} tab
   */
  select(tab) {
    const index = this.refs.tabs.indexOf(tab);
    if (index === -1) return;

    this.refs.tabs.forEach((item, i) => {
      const selected = i === index;
      item.setAttribute('aria-selected', String(selected));
      item.tabIndex = selected ? 0 : -1;
    });

    this.refs.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    const track = this.refs.tracks[index];
    if (track) {
      track.scrollTo({ left: 0, behavior: 'instant' });
      this.#updateProgress(track);
    }
  }

  /**
   * Arrow/Home/End navigation between tabs (WAI-ARIA tabs pattern).
   * @param {KeyboardEvent} event
   */
  #handleTabKeydown = (event) => {
    const { tabs } = this.refs;
    const current = tabs.indexOf(/** @type {HTMLButtonElement} */ (event.currentTarget));
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let next;

    switch (event.key) {
      case 'ArrowRight':
        next = current + (isRtl ? -1 : 1);
        break;
      case 'ArrowLeft':
        next = current + (isRtl ? 1 : -1);
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = tabs.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const tab = tabs[(next + tabs.length) % tabs.length];
    if (!tab) return;

    tab.focus();
    this.select(tab);
  };

  /** Pixels the pointer must move before a press becomes a drag (so plain clicks still open products) */
  static DRAG_THRESHOLD = 5;

  /** @type {boolean} */
  #dragged = false;

  /**
   * Mouse drag-to-scroll. Touch and pen already scroll natively, so only mouse input is handled.
   * Scroll snapping is disabled while dragging and restored on release so the track settles on a card.
   * @param {PointerEvent} event
   */
  #handleDragStart = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    const track = /** @type {HTMLElement} */ (event.currentTarget);
    if (track.scrollWidth <= track.clientWidth) return;

    const startX = event.clientX;
    const startScroll = track.scrollLeft;
    this.#dragged = false;

    /** @param {PointerEvent} moveEvent */
    const onMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX;

      if (!this.#dragged) {
        if (Math.abs(delta) < FeaturedCollectionsComponent.DRAG_THRESHOLD) return;
        this.#dragged = true;
        track.setPointerCapture(event.pointerId);
        track.style.scrollSnapType = 'none';
        track.style.cursor = 'grabbing';
      }

      track.scrollLeft = startScroll - delta;
    };

    const onEnd = () => {
      track.removeEventListener('pointermove', onMove);
      track.removeEventListener('pointerup', onEnd);
      track.removeEventListener('pointercancel', onEnd);

      if (!this.#dragged) return;

      track.style.cursor = '';
      // Restore snapping from the current position; snap settles on the nearest card
      const position = track.scrollLeft;
      track.style.scrollSnapType = '';
      track.scrollLeft = position;
    };

    track.addEventListener('pointermove', onMove);
    track.addEventListener('pointerup', onEnd);
    track.addEventListener('pointercancel', onEnd);
  };

  /**
   * Swallows the click that follows a drag so releasing over a card does not open the product.
   * @param {MouseEvent} event
   */
  #handleClickAfterDrag = (event) => {
    if (!this.#dragged) return;

    event.preventDefault();
    event.stopPropagation();
    this.#dragged = false;
  };

  #updateAllProgress() {
    for (const track of this.refs.tracks) this.#updateProgress(track);
  }

  /**
   * Sizes and positions the progress thumb to reflect the visible part of the track.
   * @param {HTMLElement} track
   */
  #updateProgress(track) {
    const index = this.refs.tracks.indexOf(track);
    const progress = this.refs.progress[index];
    if (!progress) return;

    const { scrollWidth, clientWidth } = track;
    const maxScroll = scrollWidth - clientWidth;

    if (maxScroll <= 1) {
      progress.hidden = true;
      return;
    }

    progress.hidden = false;

    const visibleRatio = clientWidth / scrollWidth;
    const scrolled = Math.min(Math.abs(track.scrollLeft) / maxScroll, 1);

    progress.style.setProperty('--fc-thumb-size', `${visibleRatio * 100}%`);
    progress.style.setProperty('--fc-thumb-offset', `${scrolled * (1 - visibleRatio) * 100}%`);
  }
}

if (!customElements.get('featured-collections-component')) {
  customElements.define('featured-collections-component', FeaturedCollectionsComponent);
}
